import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Loader2, CalendarClock, Package, Banknote, Sparkles, Boxes, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/format";

export default function LocationHubPanel() {
  const [loc, setLoc] = useState("");
  const { data: locations = [] } = useQuery({ queryKey: ["mine-locations"], queryFn: () => api.get("/admin/locations/mine").then((r) => r.data) });
  const activeLoc = loc || locations[0]?.id || "";
  const { data, isLoading } = useQuery({
    queryKey: ["location-hub", activeLoc],
    queryFn: () => api.get(`/admin/locations/${activeLoc}/hub`).then((r) => r.data),
    enabled: !!activeLoc,
  });

  return (
    <div data-testid="admin-location-hub-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><Building2 size={22} className="text-[#144F75]" /> Location Business Hub</h2>
          <p className="text-sm text-[#4A5D6B]">Run each center's bookings, staff, inventory & payroll in one place.</p>
        </div>
        <select value={activeLoc} onChange={(e) => setLoc(e.target.value)} data-testid="hub-location" className="eyw-input !py-2 text-sm">
          {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>

      {isLoading || !data ? <Loader2 className="animate-spin text-[#144F75]" /> : (
        <div className="grid md:grid-cols-2 gap-5">
          <Card icon={CalendarClock} title="Today's staff schedule" testid="hub-shifts">
            {data.shifts_today.length === 0 ? <Empty>No shifts scheduled today.</Empty> : data.shifts_today.map((s) => (
              <Row key={s.id} left={`${s.start_time}–${s.end_time} · ${s.role || "Shift"}`} right={s.enhancer_name || "Unassigned"} />
            ))}
            <p className="text-xs text-[#8FA89B] mt-2">{data.open_shifts_this_week} open shift(s) to fill this week.</p>
          </Card>

          <Card icon={Banknote} title="Payroll" testid="hub-payroll">
            <Row left="Unpaid total" right={`${money(data.payroll.unpaid_total)}`} />
            <Row left="Runs on file" right={data.payroll.runs} />
            {data.payroll.last_run && <Row left="Last run" right={`${data.payroll.last_run.period_start} → ${data.payroll.last_run.period_end}`} />}
          </Card>

          <Card icon={Package} title="Inventory alerts" testid="hub-inventory">
            {data.low_stock.length === 0 ? <Empty>All products well stocked.</Empty> : data.low_stock.map((p) => (
              <Row key={p.id} left={p.title} right={<span className="text-[#B5794E]">{p.stock} left</span>} />
            ))}
          </Card>

          <Card icon={Sparkles} title="Upcoming events" testid="hub-events">
            {data.upcoming_events.length === 0 ? <Empty>No upcoming events here.</Empty> : data.upcoming_events.map((e) => (
              <Row key={e.id} left={e.title} right={e.date} />
            ))}
          </Card>
        </div>
      )}

      {activeLoc && <InventoryManager locationId={activeLoc} />}
    </div>
  );
}

function InventoryManager({ locationId }) {
  const qc = useQueryClient();
  const [edits, setEdits] = useState({});
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["loc-inventory", locationId],
    queryFn: () => api.get(`/admin/locations/${locationId}/inventory`).then((r) => r.data),
    enabled: !!locationId,
  });

  const val = (pid, key, fallback) => (edits[pid]?.[key] ?? fallback);
  const set = (pid, key, v) => setEdits((e) => ({ ...e, [pid]: { ...e[pid], [key]: v } }));
  const save = async (it) => {
    try {
      await api.put(`/admin/locations/${locationId}/inventory`, {
        product_id: it.product_id,
        stock: parseInt(val(it.product_id, "stock", it.stock), 10) || 0,
        reorder_threshold: parseInt(val(it.product_id, "reorder_threshold", it.reorder_threshold), 10) || 0,
      });
      toast.success(`Saved ${it.title}`);
      setEdits((e) => { const n = { ...e }; delete n[it.product_id]; return n; });
      qc.invalidateQueries({ queryKey: ["loc-inventory", locationId] });
      qc.invalidateQueries({ queryKey: ["location-hub", locationId] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="eyw-card p-5 mt-5" data-testid="hub-inventory-manager">
      <h3 className="font-serif text-lg text-[#1A242B] mb-1 flex items-center gap-2"><Boxes size={18} className="text-[#144F75]" /> This center's inventory</h3>
      <p className="text-xs text-[#8FA89B] mb-4">Stock and low-stock thresholds are tracked per location. Set each product's on-hand count and the level at which it should alert.</p>
      {isLoading ? <Loader2 className="animate-spin text-[#144F75]" /> : items.length === 0 ? (
        <Empty>No physical products in the catalog yet.</Empty>
      ) : (
        <div className="space-y-2">
          {items.map((it) => {
            const dirty = !!edits[it.product_id];
            return (
              <div key={it.product_id} data-testid={`inv-row-${it.product_id}`} className="flex flex-wrap items-center gap-3 border-b border-[#E5E0D8] last:border-0 py-2">
                <span className="text-sm text-[#1A242B] flex-1 min-w-[140px] truncate flex items-center gap-2">
                  {it.title}
                  {it.low && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#B5794E]/15 text-[#B5794E]">Low</span>}
                </span>
                <label className="text-xs text-[#4A5D6B] flex items-center gap-1">Stock
                  <input type="number" min={0} value={val(it.product_id, "stock", it.stock)} onChange={(e) => set(it.product_id, "stock", e.target.value)} data-testid={`inv-stock-${it.product_id}`} className="eyw-input !py-1 !w-20 text-sm" />
                </label>
                <label className="text-xs text-[#4A5D6B] flex items-center gap-1">Alert at
                  <input type="number" min={0} value={val(it.product_id, "reorder_threshold", it.reorder_threshold)} onChange={(e) => set(it.product_id, "reorder_threshold", e.target.value)} data-testid={`inv-threshold-${it.product_id}`} className="eyw-input !py-1 !w-20 text-sm" />
                </label>
                <button onClick={() => save(it)} disabled={!dirty} data-testid={`inv-save-${it.product_id}`} className="eyw-btn !py-1.5 text-xs disabled:opacity-40 flex items-center gap-1"><Save size={13} /> Save</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const Card = ({ icon: Icon, title, testid, children }) => (
  <div className="eyw-card p-5" data-testid={testid}>
    <h3 className="font-serif text-lg text-[#1A242B] mb-3 flex items-center gap-2"><Icon size={18} className="text-[#144F75]" /> {title}</h3>
    <div className="space-y-1.5">{children}</div>
  </div>
);
const Row = ({ left, right }) => (
  <div className="flex items-center justify-between text-sm border-b border-[#E5E0D8] last:border-0 py-1.5">
    <span className="text-[#4A5D6B] truncate pr-2">{left}</span><span className="text-[#1A242B] font-medium shrink-0">{right}</span>
  </div>
);
const Empty = ({ children }) => <p className="text-sm text-[#8FA89B]">{children}</p>;
